import { Button, Flex, Text } from '@wyz/ui';
import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  type HTMLAttributes,
} from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { CheckboxField } from '~/common/components';
import { Trans, useI18n } from '~/common/context';
import { IUserCustomer } from '~/common/types';
import { SearchErrors, SearchValuePreview } from '~/purchase/components';
import { PurchaseSearchHelpers } from '~/purchase/helpers';
import { useSearchTrigger } from '~/purchase/hooks';
import { SEARCH_TYPE_ENUM } from '~/purchase/schemas';
import { IBaseSearchFormSchema, IQuoteTyreOffer } from '~/purchase/types';

interface SearchTriggerContextValue {
  canSearch: boolean;
  isProcessing: boolean;
  count: number | undefined;
  maxSearchExceeded: boolean;
  hasNoResults: boolean;
}

const SearchTriggerContext = createContext<
  SearchTriggerContextValue | undefined
>(undefined);

const useSearchTriggerContext = () => {
  const context = useContext(SearchTriggerContext);
  if (!context) {
    throw new Error(
      'SearchTrigger compound components must be used within SearchTrigger',
    );
  }
  return context;
};

interface SearchTriggerProps {
  customer: IUserCustomer;
  express?: boolean;
  children: ReactNode;
}

function SearchTrigger({
  customer,
  express = false,
  children,
}: SearchTriggerProps) {
  const { canSearch, count, errors } = useSearchTrigger<IQuoteTyreOffer>({
    searchType: SEARCH_TYPE_ENUM.QUOTE,
    customer,
    express,
  });

  const contextValue = useMemo<SearchTriggerContextValue>(
    () => ({
      canSearch,
      isProcessing: count.isProcessing,
      count: count.data?.data,
      maxSearchExceeded: errors.maxSearchExceeded,
      hasNoResults: errors.hasNoResults,
    }),
    [
      canSearch,
      count.isProcessing,
      count.data?.data,
      errors.hasNoResults,
      errors.maxSearchExceeded,
    ],
  );

  return (
    <SearchTriggerContext.Provider value={contextValue}>
      {children}
    </SearchTriggerContext.Provider>
  );
}

interface LayoutProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

function Layout({ children, className = '', ...props }: LayoutProps) {
  return (
    <div className={`search-bar-trigger row mt-3 ${className}`} {...props}>
      {children}
    </div>
  );
}

function Body({ express = false }: { express?: boolean }) {
  const { t } = useI18n();
  const methods = useFormContext<IBaseSearchFormSchema>();
  const watchedValues = useWatch({
    control: methods.control,
  }) as IBaseSearchFormSchema;
  const { canSearch, isProcessing } = useSearchTriggerContext();

  return (
    <Flex alignItems='center' justifyContent='between'>
      <SearchValuePreview
        label={<Trans i18nKey='app.purchase.search_page.selected_dimension' />}
        value={
          PurchaseSearchHelpers.formatDimension({
            width: watchedValues.width,
            height: watchedValues.height,
            diameter: watchedValues.diameter,
            loadIndex: watchedValues.loadIndexes?.[0],
            speedIndex: watchedValues.speedIndexes?.[0],
            identifier: watchedValues.identifier?.value,
          }) ?? t('app.common.none')
        }
      />

      <Flex alignItems='center' className='space-x-2' wrap='wrap'>
        {!express && (
          <CheckboxField
            label={
              <Text heading='xs' color='secondary-gray'>
                <Trans i18nKey='app.common.search.include_higher_indexes' />
              </Text>
            }
            name='includeHigherIndexes'
            disabled={!watchedValues.vehicleTypeGroup}
            formGroupClassName=''
            asTextField={false}
          />
        )}
        {[1, 2, 4].map((quantity) => (
          <Button
            key={quantity}
            id={`select-${quantity}-quantity`}
            variant={
              watchedValues.quantity === quantity
                ? 'outline-primary'
                : 'neutral'
            }
            onClick={() => methods.setValue('quantity', quantity)}
          >
            <span className='no-wrap'>
              <Trans
                i18nKey='app.quote.tyre_count'
                values={{ count: quantity }}
              />
            </span>
          </Button>
        ))}
        <Button
          type='submit'
          id={express ? 'cta-generate-express-quote' : 'search'}
          variant='primary'
          disabled={!canSearch}
          isLoading={isProcessing}
          form='quote-search-form'
        >
          <Trans
            i18nKey={
              express
                ? 'app.purchase.search_page.create_express_quote'
                : 'app.common.buttons.search'
            }
          />
        </Button>
      </Flex>
    </Flex>
  );
}

function ErrorSection() {
  const { maxSearchExceeded, hasNoResults, count } = useSearchTriggerContext();

  return (
    <SearchErrors
      maxSearchExceeded={maxSearchExceeded}
      hasNoResults={hasNoResults}
      count={count}
    />
  );
}

const QuoteSearchTriggerBar = Object.assign(SearchTrigger, {
  Layout,
  Body,
  ErrorSection,
});

export default QuoteSearchTriggerBar;
